import {useMutation} from '@tanstack/react-query'
import {sha256} from 'js-sha256'
import {useSignIn} from 'react-auth-kit'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import useRequestParams from 'src/feature/bite/hook/request'
import { useIsMobile } from 'src/feature/bite/hook/useIsMobile'

export function useLogin() {
  const {headers} = useRequestParams()
  const navigate = useNavigate()
  const signIn = useSignIn()
  const intl = useIntl()
  const isMobile = useIsMobile()

  return useMutation(['login'], async ({values, recaptcha}: {values: any; recaptcha: string}) => {
    toast.dismiss()

    if (values.recaptcha === '' || !values.recaptcha || values.recaptcha === recaptcha) {
      toast.error(intl.formatMessage({id: 'CAPTCHA.ERROR'}), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: 'light',
      })
      return false
    }
    const timeZoneOffset = getTimeZone()

    const Response = await fetch(`${API}/Auth/Login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json-patch+json',
      },
      body: JSON.stringify({
        ...values,
        password: sha256(values.password),
        language: headers.Language,
        timeZoneOffSet: timeZoneOffset,
      }),
    })

    const data = await Response.json()

    if (!data.responseResult.result) {
      toast.error(
        data?.responseResult?.exceptionDetail || intl.formatMessage({id: 'LOGIN.ERROR'}),
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        }
      )

      if (typeof document !== 'undefined' && (window as any).grecaptcha) {
        ;(window as any).grecaptcha.reset()
      }
      return
    }

    const token = data.response.token
    const forceChangePassword = data.response.newPassword

    if (forceChangePassword) {
      navigate('/new-password', {
        state: {
          email: values.email,
        },
      })
      return
    }

    if (
      signIn({
        token: token.token,
        expiresIn: 3600 * 24 * 1000,
        tokenType: 'Bearer',
        authState: {
          fullName: token.fullName,
          id: token.userID,
          name: values.email,
          email: values.email,
          token: token.token,
          firstLogin: data.response.firstLogin,
          merchantID: data.response.merchantID,
          merchantBusinessType: data.response.merchantBusinessType,
        },
        // refreshToken: token.refreshToken,
        // refreshTokenExpireIn: 3600 * 24 * 1000 * 30,
      })
    ) {
      // Redirect or do-something
      if (isMobile) {
        navigate('/members')
      } else {
        navigate('/dashboard')
      }
    }
  })
}

export function useForgotPassword() {
  const intl = useIntl()
  const {headers} = useRequestParams()

  return useMutation(
    ['forgot-password'],
    async ({values, recaptcha}: {values: any; recaptcha: string}) => {
      if (values.recaptcha === '' || !values.recaptcha || values.recaptcha === recaptcha) {
        toast.error(intl.formatMessage({id: 'CAPTCHA.ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      const Response = await fetch(`${API}/Auth/ResetPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json-patch+json',
        },
        body: JSON.stringify({
          email: values.email,
          recaptcha: values.recaptcha,
          language: headers.Language,
        }),
      })

      const data = await Response.json()

      if (!data.response.isMailSent || !data.responseResult.result) {
        toast.error(
          data?.responseResult?.exceptionDetail || intl.formatMessage({id: 'FORGOT.ERROR'}),
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: 'light',
          }
        )
      } else {
        toast.success(intl.formatMessage({id: 'FORGOT.SUCCESS'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      }

      if (typeof document !== 'undefined' && (window as any).grecaptcha) {
        ;(window as any).grecaptcha.reset()
      }
    }
  )
}

export function useCreatePassword() {
  const intl = useIntl()
  const {headers} = useRequestParams()

  return useMutation(
    ['create-password'],
    async ({values, code, pendinguserID}: {values: any; code: string; pendinguserID: string}) => {
      const Response = await fetch(`${API}/Auth/CreatePassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json-patch+json',
        },
        body: JSON.stringify({
          code,
          pendinguserID,
          password: sha256(values.password),
          language: headers.Language,
        }),
      })

      const data = await Response.json()
      if (!data.responseResult.result) {
        toast.error(intl.formatMessage({id: 'CREATE_PASSWORD.ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      } else {
        toast.success(intl.formatMessage({id: 'CREATE_PASSWORD.SUCCESS'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })

        setTimeout(() => {
          window.location.href = '/sign-in'
        }, 3000)
      }
    }
  )
}

export function useChangePassword() {
  const intl = useIntl()
  const {auth, headers} = useRequestParams()

  const userID = auth()?.id

  return useMutation(['create-password'], async ({values, email}: {values: any; email: string}) => {
    const timeZoneOffset = getTimeZone()

    const Response = await fetch(`${API}/Auth/ChangePassword`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userID,
        email,
        oldPassword: sha256(values.oldPassword),
        newPassword: sha256(values.password),
        language: headers.Language,
        timeZoneOffSet: timeZoneOffset,
      }),
    })

    const data = await Response.json()
    if (!data.responseResult.result) {
      toast.error(
        data?.responseResult?.exceptionDetail || intl.formatMessage({id: 'UPDATE_PASSWORD.ERROR'}),
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        }
      )
    } else {
      toast.success(
        data?.responseResult?.exceptionDetail ||
          intl.formatMessage({id: 'UPDATE_PASSWORD.SUCCESS'}),
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        }
      )

      setTimeout(() => {
        window.location.href = '/'
      }, 3000)
    }
  })
}

export function useFirstLogin() {
  const intl = useIntl()
  const {auth, headers} = useRequestParams()

  const userID = auth()?.id

  return useMutation(['first-login'], async () => {
    const Response = await fetch(`${API}/Merchant/MerchantFirstLogin`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userID,
        language: headers.Language,
      }),
    })

    const data = await Response.json()
    toast.success(
      data?.responseResult?.exceptionDetail || intl.formatMessage({id: 'FIRST_LOGIN.SUCCESS'}),
      {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: 'light',
      }
    )

    return true
  })
}
